import { useState, useEffect, useRef } from "react";

interface DataPoint {
  date: string;
  isSelected?: boolean;
  [key: string]: any;
}

/**
 * Custom hook to manage chart point selection state and interactions
 * Handles selected/hovered points and provides click handler
 */
export function useChartSelection<T extends DataPoint>(data: T[]) {
  // Find the first selected point, or default to first data point
  const initialSelectedPoint =
    data.find((point) => point.isSelected) || data[0];

  const [selectedPoint, setSelectedPoint] = useState<T>(initialSelectedPoint);
  const [hoveredPoint, setHoveredPoint] = useState<T | null>(null);
  const hasAnimated = useRef(false);

  // Update selected point when data changes and a point has isSelected
  useEffect(() => {
    const newSelectedPoint = data.find((point) => point.isSelected);
    if (newSelectedPoint) {
      setSelectedPoint(newSelectedPoint);
    }
  }, [data]);

  const handlePointClick = (clickData: any) => {
    if (clickData && clickData.activePayload) {
      setSelectedPoint(clickData.activePayload[0].payload);
    }
  };

  const activePoint = hoveredPoint || selectedPoint;
  const activeIndex = data.findIndex((d) => d.date === activePoint.date);

  return {
    selectedPoint,
    setSelectedPoint,
    hoveredPoint,
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  };
}
